import { config } from "../../../app-confi/app-config";
import { columnType } from "./models/column-type.enum";
import { BuildingHelperService } from "../../../app-confi/services/building-helper.service";
import { SceneManager } from "../../../common/scene.manager";
import { BaseBabylonService } from "../base-babylon.service";
import { partTypeEnum } from "./models/part-type.enum";
import { accuracyEnum } from "../../../common/models/accuracy.enum";
import { RowhouseColumn, RowhousePart } from "./models/props-config";
import { buildingType } from "../models/building-type.enum";

export class RowhouseBabylonService extends BaseBabylonService {

    private bhs: BuildingHelperService;

    constructor(
        sceneManager: SceneManager,
        buildingHelperService: BuildingHelperService,
    ) {
        super(sceneManager);
        this.bhs = buildingHelperService;

    }

    convertPartsToColumns(parts: RowhousePart[]): RowhouseColumn[] {
        return parts.map(s => s.columns).flat();
    }

    renderColumnsTo3D(
        buildingId: string | number,
        parts: RowhousePart[],
        locationInSite,
        accuracy: accuracyEnum
    ): Promise<any> {
        const promises = [];

        if (parts && parts.length > 0) {
            const partBody = parts.find(p => p.type === partTypeEnum.body);
            const bodyColumns = partBody.columns;
            if (bodyColumns && bodyColumns.length > 0) {
                const floorHeight = this.bhs.floorHeightOfBuilding(buildingType.rowhouse);

                bodyColumns.forEach(column => {
                    const renderPromises = this.renderInstances(buildingId, column.id, column.unitsAndAmenities, locationInSite, partBody.partTransformation, accuracy, floorHeight);
                    promises.push(...renderPromises);
                })
            }
        }
        return Promise.all(promises);
    }

    mapPartsTo3D(partsIn: RowhousePart[], doNotOrder?: boolean): RowhousePart[] {
        // make copy, dont manipulate existing columns
        const p = partsIn.find(p => p.type === partTypeEnum.body);
        if (p && p.columns && p.columns.length > 0) {
            let columns = [...p.columns.map(c => ({ ...c }))];

            if (columns && columns.length > 0) {

                let columnsInOrder: RowhouseColumn[];
                if (doNotOrder) {
                    columnsInOrder = columns;
                } else {
                    columnsInOrder = this.orderColumns(columns);
                }

                let offsetBasedOnColumn = 0;

                columnsInOrder.forEach((column) => {
                    column.unitsAndAmenities = [];
                    const columnSegments = column.segments;
                    columnSegments.forEach(segment => {
                        const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, 0);
                        column.unitsAndAmenities.push(unitsAndAmenities);
                    })
                    offsetBasedOnColumn = offsetBasedOnColumn + column.bays * config.rowhouseBaySize;
                });

                const part: RowhousePart = {
                    type: partTypeEnum.body,
                    columns: columnsInOrder
                };

                return [part];
            }
        }
        return [{
            type: partTypeEnum.body,
            columns: []
        }];
    }

    private orderColumns(columns: RowhouseColumn[]) {
        const columnsInOrder: RowhouseColumn[] = [];

        // add all in betweens
        const allExceptHeadsStaircasesAndMoosPlein = columns.filter(c => (c.type === columnType.H_S || c.type === columnType.H_L || c.type === columnType.H_M1M2));

        const shuffledColumns = this.equalizeColumns(allExceptHeadsStaircasesAndMoosPlein);

        columnsInOrder.push(...shuffledColumns);

        columnsInOrder.forEach((c, index) => {
            c.order = index;
        })

        return columnsInOrder;
    }



    private equalizeColumns(spreadColumns: RowhouseColumn[]): RowhouseColumn[] {
        const h_sColumns = spreadColumns.filter(c => c.type === columnType.H_S);
        const h_m1m2Columns = spreadColumns.filter(c => c.type === columnType.H_M1M2);
        const h_lColumns = spreadColumns.filter(c => c.type === columnType.H_L);

        const shuffledColumns = [];
        const equalize = () => {
            if (h_sColumns.length > 0) {
                shuffledColumns.push(h_sColumns.pop());
            }
            if (h_m1m2Columns.length > 0) {
                shuffledColumns.push(h_m1m2Columns.pop());
            }
            if (h_lColumns.length > 0) {
                shuffledColumns.push(h_lColumns.pop());
            }
            if (shuffledColumns.length !== spreadColumns.length) {
                equalize();
            }
        };
        equalize();
        return shuffledColumns;
    }
}